import { useEffect, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { api, money } from "@/lib/api";
import { ContactPicker } from "@/components/ContactPicker";
import { SearchSelect } from "@/components/SearchSelect";
import { ProductFormModal } from "@/pages/Products";

const todayStr = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const emptyLine = () => ({ product_id: "", product_name: "", description: "", quantity: 1, unit: "", price: "", discount_percent: "" });
const lineAmount = (l) => Math.max(0, (Number(l.quantity) || 0) * (Number(l.price) || 0) * (1 - (Number(l.discount_percent) || 0) / 100));

export default function TransactionForm({ cfg, initial, onCancel, onSave, saving }) {
  const isSales = cfg.endpoint === "/invoices";
  const contactField = isSales ? "customer" : "supplier";
  const [products, setProducts] = useState([]);
  const [head, setHead] = useState({
    contact: initial?.[contactField] || "",
    email: initial?.email || "",
    date: initial?.date || todayStr(),
    due_date: initial?.due_date || plusDays(30),
    reference: initial?.reference || "",
    memo: initial?.memo || "",
    tax_percent: initial?.tax_percent || 0,
  });
  const [lines, setLines] = useState(
    initial?.lines?.length
      ? initial.lines.map((l) => ({ product_id: l.product_id || "", product_name: l.product_name || "", description: l.description || "", quantity: l.quantity ?? 1, unit: l.unit || "", price: l.price ?? (l.quantity ? (l.amount || 0) / l.quantity : l.amount || 0), discount_percent: l.discount_percent || "" }))
      : [emptyLine()]
  );
  const [quickAdd, setQuickAdd] = useState(null);
  useEffect(() => {
    api("/products").then(setProducts).catch(() => {});
  }, [isSales]);
  const set = (k, v) => setHead({ ...head, [k]: v });
  const setLine = (i, k, v) => setLines(lines.map((l, x) => (x === i ? { ...l, [k]: v } : l)));
  const pickProduct = (i, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) { setLine(i, "product_id", ""); return; }
    setLines(lines.map((l, x) => (x === i ? { ...l, product_id: p.id, product_name: p.name, description: p.variant ? `${p.name} — ${p.variant}` : p.name, unit: p.unit || "pcs", price: isSales ? p.selling_price : p.purchase_price } : l)));
  };
  const pickQuickProduct = (r) => {
    setProducts((prev) => [r, ...prev]);
    const i = quickAdd.lineIndex;
    setLines((prev) => prev.map((l, x) => (x === i ? { ...l, product_id: r.id, product_name: r.name, description: r.name, unit: r.unit || "pcs", price: isSales ? r.selling_price : r.purchase_price } : l)));
    setQuickAdd(null);
  };
  const subtotal = lines.reduce((s, l) => s + lineAmount(l), 0);
  const taxAmount = Math.round(subtotal * (Number(head.tax_percent) || 0) / 100);
  const total = subtotal + taxAmount;
  const filledLines = lines.filter((l) => lineAmount(l) > 0);
  const valid = head.contact && head.date && head.due_date && total > 0 && filledLines.every((l) => l.product_id || l.description);
  const submit = (approve) => {
    const payload = {
      [contactField]: head.contact,
      email: head.email, date: head.date, due_date: head.due_date, reference: head.reference, memo: head.memo,
      description: head.memo || `${isSales ? "Penjualan" : "Pembelian"} ${filledLines.map((l) => l.product_name || l.description).join(", ").slice(0, 120)}`,
      tax_percent: Number(head.tax_percent) || 0, subtotal, tax_amount: taxAmount, total,
      lines: filledLines.map((l) => ({ product_id: l.product_id, product_name: l.product_name, description: l.description, quantity: Number(l.quantity) || 0, unit: l.unit, price: Number(l.price) || 0, discount_percent: Number(l.discount_percent) || 0, amount: lineAmount(l) })),
    };
    onSave(payload, approve);
  };
  return (
    <div className="form-panel transaction-form" data-testid="transaction-form">
      <div className="section-head">
        <div><p className="eyebrow">{isSales ? "PENJUALAN" : "PEMBELIAN"}</p><h2>{initial?.id ? "Edit" : "Buat"} {cfg.singular}</h2></div>
        <button data-testid="transaction-cancel-button" className="icon-btn" onClick={onCancel}><X size={18} /></button>
      </div>
      <div className="form-grid transaction-head-grid">
        <label>{isSales ? "Pelanggan" : "Supplier"} *
          <ContactPicker
            value={head.contact}
            onPick={(c) => setHead((prev) => ({ ...prev, contact: c.name, email: c.email || prev.email }))}
            typeFilter={isSales ? "Customer" : "Supplier"}
            addLabel={isSales ? "Pelanggan baru" : "Supplier baru"}
            defaultTypes={[isSales ? "Customer" : "Supplier"]}
            testPrefix="transaction-contact"
            placeholder={`Pilih ${isSales ? "pelanggan" : "supplier"}...`} />
        </label>
        <label>Email<input data-testid="transaction-email-input" type="email" value={head.email} onChange={(e) => set("email", e.target.value)} placeholder="Opsional" /></label>
        <label>Tanggal transaksi *<input data-testid="transaction-date-input" type="date" value={head.date} onChange={(e) => set("date", e.target.value)} /></label>
        <label>Tanggal jatuh tempo *<input data-testid="transaction-due-date-input" type="date" value={head.due_date} onChange={(e) => set("due_date", e.target.value)} /></label>
        <label>No. referensi<input data-testid="transaction-reference-input" value={head.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Opsional" /></label>
        <label>Memo<input data-testid="transaction-memo-input" value={head.memo} onChange={(e) => set("memo", e.target.value)} placeholder="Opsional" /></label>
      </div>
      <table className="journal-line-table transaction-line-table" data-testid="transaction-line-table">
        <thead><tr><th className="col-product">PRODUK</th><th>DESKRIPSI</th><th className="right col-qty">KUANTITAS</th><th className="right col-price">HARGA SATUAN</th><th className="right col-disc">DISKON %</th><th className="right col-amount">JUMLAH</th><th /></tr></thead>
        <tbody>
          {lines.map((line, i) => (
            <tr key={i}>
              <td>
                <SearchSelect testid={`transaction-line-product-${i}`} value={line.product_id} placeholder="Pilih produk..."
                  onChange={(v) => pickProduct(i, v)}
                  options={[...(line.product_id && !products.some((p) => p.id === line.product_id) ? [{ value: line.product_id, label: line.product_name || "(produk tidak tersedia)" }] : []), ...products.map((p) => ({ value: p.id, label: `${p.name}${p.variant ? ` · ${p.variant}` : ""}`, sub: p.sku }))]}
                  onCreate={(q) => setQuickAdd({ kind: "product", lineIndex: i, name: q })} createLabel="Tambah produk baru" createTestid={`quick-add-product-${i}`} />
              </td>
              <td><input data-testid={`transaction-line-description-${i}`} value={line.description} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Deskripsi" /></td>
              <td>
                <input data-testid={`transaction-line-quantity-${i}`} className="right" type="number" min="0" value={line.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} />
                {isSales && line.product_id && (() => {
                  const p = products.find((x) => x.id === line.product_id);
                  if (!p || p.type === "Service") return null;
                  const avail = p.stock ?? 0;
                  return <small data-testid={`transaction-line-stock-${i}`} className={`stock-hint ${(Number(line.quantity) || 0) > avail ? "over" : ""}`}>Stok: {avail}</small>;
                })()}
              </td>
              <td><input data-testid={`transaction-line-price-${i}`} className="right" type="number" min="0" value={line.price} onChange={(e) => setLine(i, "price", e.target.value)} placeholder="0" /></td>
              <td><input data-testid={`transaction-line-discount-${i}`} className="right" type="number" min="0" max="100" value={line.discount_percent} onChange={(e) => setLine(i, "discount_percent", e.target.value)} placeholder="0" /></td>
              <td className="right mono" data-testid={`transaction-line-amount-${i}`}>{money(lineAmount(line))}</td>
              <td>{lines.length > 1 && <button data-testid={`transaction-line-remove-${i}`} className="icon-btn" onClick={() => setLines(lines.filter((_, x) => x !== i))}><Trash2 size={15} /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <button data-testid="transaction-add-line-button" className="text-btn add-line-btn" onClick={() => setLines([...lines, emptyLine()])}><Plus size={14} /> Tambah baris produk</button>
      <div className="transaction-totals">
        <div><span>Subtotal</span><strong data-testid="transaction-subtotal">{money(subtotal)}</strong></div>
        <div>
          <span className="tax-select-wrap">Pajak
            <select data-testid="transaction-tax-select" value={head.tax_percent} onChange={(e) => set("tax_percent", e.target.value)}>
              <option value={0}>Tanpa pajak</option>
              <option value={11}>PPN 11%</option>
            </select>
          </span>
          <strong data-testid="transaction-tax-amount">{money(taxAmount)}</strong>
        </div>
        <div className="grand-total"><span>Total</span><strong data-testid="transaction-total">{money(total)}</strong></div>
      </div>
      <div className="form-actions">
        <button data-testid="transaction-save-approve-button" className="primary-btn" disabled={!valid || saving} onClick={() => submit(true)}>{initial?.id ? "Simpan perubahan" : "Simpan & Setujui"}</button>
        {!initial?.id && <button data-testid="transaction-save-draft-button" className="secondary-btn" disabled={!valid || saving} onClick={() => submit(false)}>Simpan sebagai Draft</button>}
        <button data-testid="transaction-form-cancel-button" className="secondary-btn" onClick={onCancel}>Batal</button>
      </div>
      {quickAdd?.kind === "product" && (
        <ProductFormModal initial={null} defaultName={quickAdd.name} onClose={() => setQuickAdd(null)} onDone={pickQuickProduct} />
      )}
    </div>
  );
}

function QuickAdd({ title, testPrefix, fields, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try { await onSave(form); } catch (err) { setError(err.message); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay quick-add-overlay" data-testid={`${testPrefix}-modal`} onClick={onClose}>
      <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head">
            <div><p className="eyebrow">TAMBAH CEPAT</p><h2>{title}</h2></div>
            <button type="button" data-testid={`${testPrefix}-close`} className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
          <form onSubmit={submit}>
            <div className="form-grid">
              {fields.map((f) => (
                <label key={f.key}>{f.label}{f.required ? " *" : ""}
                  <input data-testid={`${testPrefix}-${f.key}`} type={f.type || "text"} required={f.required} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder || "Opsional"} />
                </label>
              ))}
            </div>
            {error && <div className="journal-balance bad" data-testid={`${testPrefix}-error`}>{error}</div>}
            <div className="form-actions">
              <button data-testid={`${testPrefix}-save`} className="primary-btn" type="submit" disabled={saving}>{saving ? "Menyimpan..." : "Simpan & Pilih"}</button>
              <button data-testid={`${testPrefix}-cancel`} type="button" className="secondary-btn" onClick={onClose}>Batal</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

const METHODS = ["Tunai", "Transfer Bank", "Cek", "Giro", "Kartu Kredit"];

export function DocumentPaymentModal({ mode, doc, onClose, onDone, onError }) {
  const isReceive = mode === "RECEIVE";
  const outstanding = (doc.total || 0) - (doc.paid || 0);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ account_code: "", amount: String(outstanding), date: todayStr(), method: "Transfer Bank", reference: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api("/cash-accounts").then(setAccounts).catch(() => {}); }, []);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const amount = Number(form.amount) || 0;
  const valid = form.account_code && form.date && amount > 0 && amount <= outstanding + 0.01;
  const submit = async () => {
    setSaving(true);
    try {
      const payment = await api("/payments", { method: "POST", body: JSON.stringify({ type: mode, contact: doc.customer || doc.supplier, date: form.date, account_code: form.account_code, method: form.method, reference: form.reference, allocations: [{ document_id: doc.id, amount }] }) });
      onDone(payment);
    } catch (e) { onError(e.message); }
    setSaving(false);
  };
  return (
    <div className="modal-overlay" data-testid="document-payment-modal" onClick={onClose}>
      <div className="modal-panel narrow" onClick={(e) => e.stopPropagation()}>
        <div className="form-panel">
          <div className="section-head">
            <div><p className="eyebrow">KONFIRMASI PEMBAYARAN</p><h2>{isReceive ? "Terima Pembayaran" : "Kirim Pembayaran"}</h2></div>
            <button data-testid="document-payment-close" className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
          <div className="payment-doc-summary" data-testid="document-payment-summary">
            <div><small>{doc.number}</small><strong>{doc.customer || doc.supplier}</strong></div>
            <div className="right"><small>Sisa tagihan</small><strong>{money(outstanding)}</strong></div>
          </div>
          <div className="form-grid payment-head-grid">
            <label>{isReceive ? "Setor ke akun (COA)" : "Bayar dari akun (COA)"} *
              <SearchSelect testid="document-payment-account-select" value={form.account_code} onChange={(v) => set("account_code", v)}
                placeholder="Pilih akun kas/bank..."
                options={accounts.map((a) => ({ value: a.code, label: `${a.code} · ${a.name}`, sub: money(a.balance) }))} />
            </label>
            <label>Jumlah dibayar *<input data-testid="document-payment-amount-input" type="number" min="0" max={outstanding} value={form.amount} onChange={(e) => set("amount", e.target.value)} /></label>
            <label>Tanggal pembayaran *<input data-testid="document-payment-date-input" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></label>
            <label>Metode pembayaran
              <select data-testid="document-payment-method-select" value={form.method} onChange={(e) => set("method", e.target.value)}>
                {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label>No. referensi<input data-testid="document-payment-reference-input" value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Opsional" /></label>
          </div>
          {amount > outstanding + 0.01 && <div className="journal-balance bad" data-testid="document-payment-overpaid">Jumlah melebihi sisa tagihan</div>}
          <div className="form-actions">
            <button data-testid="document-payment-save-button" className="primary-btn" disabled={!valid || saving} onClick={submit}>{saving ? "Menyimpan..." : `Konfirmasi ${money(amount)}`}</button>
            <button data-testid="document-payment-cancel-button" className="secondary-btn" onClick={onClose}>Batal</button>
          </div>
        </div>
      </div>
    </div>
  );
}
